/**
 * <bundle-plp>
 * Filter pills for sections/bundle-plp.liquid. A pill's data-bundle-plp-filter
 * is a lowercase tag ("" = All); a card is shown when its data-tags ("|a|b|")
 * contains that tag. The heading switches to the pill's data-heading ("All"
 * restores the original heading) and "[count]" in the subheading is filled
 * with the number of visible bundles. The Buy once / Subscribe switch needs
 * no JS (see blocks/bundle-plp-card.liquid).
 *
 * Add to cart (data-after-add, section setting "After adding to cart"):
 * - "stay" (quick add): the customer stays on the page; the card's button
 *   shows the "Added" label with a tick for a moment, then reverts. The
 *   header cart count still updates through the theme's cartUpdate event.
 * - "checkout": goes to /checkout, the same as the bundle PDP
 *   (assets/main-product-bundle.js, which explains the approach in full).
 * - "drawer": the theme's default, the cart drawer opens.
 * - "added_drawer": the button shows "Added" + tick (as in "stay") and the
 *   cart drawer opens as usual.
 * The theme's ProductForm always calls cart-drawer's show() right after it
 * fires ajaxProduct:added, so for "stay" and "checkout" show() is a no-op
 * while one of this section's cards is adding.
 */
class BundlePlp extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onFormSubmitCapture = this.onFormSubmitCapture.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onAjaxProductAdded = this.onAjaxProductAdded.bind(this);
    this.onAjaxProductError = this.onAjaxProductError.bind(this);
  }

  connectedCallback() {
    this.heading = this.querySelector('[data-bundle-plp-heading]');
    this.defaultHeading = this.heading ? this.heading.textContent : '';
    this.countText = this.querySelector('[data-bundle-plp-count-text]');

    this.addEventListener('click', this.onClick);

    this.afterAdd = this.dataset.afterAdd || 'stay';
    this.pending = []; // card buttons waiting on ProductForm, oldest first
    this.revertTimers = new Map();

    if (this.afterAdd !== 'drawer') {
      this.addEventListener('submit', this.onFormSubmitCapture, true);
      this.addEventListener('submit', this.onFormSubmit);
      document.addEventListener('ajaxProduct:added', this.onAjaxProductAdded);
      document.addEventListener('ajaxProduct:error', this.onAjaxProductError);
    }

    const activePill = this.querySelector('[data-bundle-plp-filter][aria-pressed="true"]');
    if (activePill) this.applyFilter(activePill, false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('submit', this.onFormSubmitCapture, true);
    this.removeEventListener('submit', this.onFormSubmit);
    document.removeEventListener('ajaxProduct:added', this.onAjaxProductAdded);
    document.removeEventListener('ajaxProduct:error', this.onAjaxProductError);
    this.revertTimers.forEach((timer, button) => {
      clearTimeout(timer);
      this.revertButton(button);
    });
    this.restoreCartDrawer();
  }

  cardButton(form) {
    return form.closest('[data-bundle-plp-card]') ? form.querySelector('.bundle-plp-card__button') : null;
  }

  // Runs before ProductForm's own submit listener: a button that is already
  // aria-disabled is mid-add (e.g. a double click), and ProductForm will
  // ignore this submit, so it mustn't be queued a second time.
  onFormSubmitCapture(event) {
    const button = this.cardButton(event.target);
    if (button) button.dataset.wasBusy = String(button.hasAttribute('aria-disabled'));
  }

  // Runs after ProductForm has started its fetch, so the button is queued
  // before ajaxProduct:added/error can fire. A submit ProductForm didn't
  // take over (not preventDefault'ed, e.g. cart type "page") is left alone.
  onFormSubmit(event) {
    const button = this.cardButton(event.target);
    if (!button) return;
    const wasBusy = button.dataset.wasBusy === 'true';
    delete button.dataset.wasBusy;
    if (wasBusy || !event.defaultPrevented) return;

    this.pending.push(button);
    // "added_drawer" lets ProductForm open the drawer as normal
    if (this.afterAdd !== 'added_drawer') this.suppressCartDrawer();
  }

  onAjaxProductAdded() {
    const button = this.pending.shift();
    if (!button) return;

    if (this.afterAdd === 'checkout') {
      // Leave the drawer suppressed: the page is unloading.
      window.location.href = '/checkout';
      return;
    }

    this.showAdded(button);
    // ProductForm calls cartDrawer.show() synchronously after this event, so
    // restore it on the next task, once no other card is still adding.
    setTimeout(() => {
      if (!this.pending.length) this.restoreCartDrawer();
    });
  }

  onAjaxProductError() {
    if (!this.pending.shift()) return;
    if (!this.pending.length) this.restoreCartDrawer();
  }

  showAdded(button) {
    const text = button.querySelector('.btn-text');
    if (!text) return;

    clearTimeout(this.revertTimers.get(button));
    if (button.dataset.label === undefined) button.dataset.label = text.innerHTML;

    const label = document.createElement('span');
    label.textContent = this.dataset.addedLabel || 'Added';
    text.replaceChildren(label);
    text.insertAdjacentHTML(
      'beforeend',
      '<svg class="bundle-plp-card__tick" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="M2.5 7.5l3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    );
    button.classList.add('is-added');

    const status = this.querySelector('[data-bundle-plp-status]');
    if (status) {
      const name = button.closest('[data-bundle-plp-card]')?.querySelector('.bundle-plp-card__name')?.textContent.trim();
      status.textContent = [name, label.textContent].filter(Boolean).join(': ');
    }

    this.revertTimers.set(
      button,
      setTimeout(() => this.revertButton(button), 2000),
    );
  }

  revertButton(button) {
    this.revertTimers.delete(button);
    const text = button.querySelector('.btn-text');
    if (text && button.dataset.label !== undefined) text.innerHTML = button.dataset.label;
    delete button.dataset.label;
    button.classList.remove('is-added');
  }

  suppressCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || this.originalCartDrawerShow) return;
    this.originalCartDrawerShow = cartDrawer.show.bind(cartDrawer);
    cartDrawer.show = () => {};
  }

  restoreCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !this.originalCartDrawerShow) return;
    cartDrawer.show = this.originalCartDrawerShow;
    this.originalCartDrawerShow = null;
  }

  onClick(event) {
    const pill = event.target.closest('[data-bundle-plp-filter]');
    if (!pill || !this.contains(pill)) return;
    this.applyFilter(pill, true);
  }

  applyFilter(activePill, announce) {
    const tag = activePill.dataset.bundlePlpFilter;

    this.querySelectorAll('[data-bundle-plp-filter]').forEach((pill) => {
      pill.setAttribute('aria-pressed', String(pill === activePill));
    });

    let visible = 0;
    this.querySelectorAll('[data-bundle-plp-card]').forEach((card) => {
      const show = !tag || (card.dataset.tags || '').includes(`|${tag}|`);
      // Hide the Shopify block wrapper so the grid doesn't keep an empty cell.
      (card.closest('.shopify-block') || card).hidden = !show;
      // Theme Editor placeholders (no product chosen) aren't bundles, so don't count them.
      if (show && !card.classList.contains('bundle-plp-card--empty')) visible += 1;
    });

    if (this.heading) this.heading.textContent = tag ? activePill.dataset.heading || activePill.textContent.trim() : this.defaultHeading;

    if (this.countText) {
      const template = visible === 1 ? this.countText.dataset.singular : this.countText.dataset.plural;
      this.countText.textContent = template.replaceAll('[count]', visible);
      this.countText.hidden = false;
    }

    const empty = this.querySelector('[data-bundle-plp-empty]');
    if (empty) empty.hidden = visible > 0;

    const status = this.querySelector('[data-bundle-plp-status]');
    if (status && announce) {
      status.textContent = [this.heading?.textContent, this.countText?.textContent].filter(Boolean).join(', ') || `${visible}`;
    }
  }
}

customElements.define('bundle-plp', BundlePlp);
